import React, { useEffect, useState } from 'react';

import { TickedCheckboxPortal, UntickedCheckboxPortal } from 'src/assets/images';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import TealiumEvents from 'src/helpers/tealium-events';
import { InfoTray, DotLoader } from 'src/components';
import { Colors, bcaContractTermsAndConditionsUrl } from 'src/common';
import { Offer } from 'src/context/offer-context';
import './accept-contract-modal.scss';

type AcceptContractHeadProps = {
  disabled: boolean;
  onHide: () => void;
};

const AcceptContractHead = ({ disabled, onHide }: AcceptContractHeadProps) => (
  <>
    <div className="contract-header is-hidden-mobile">Business Cash Advance contract</div>

    <p className="contract-disclaimer">
      By selecting "Confirm", you agree to the Business Cash Advance Sale & Vodacom{' '}
      <a
        className="terms-and-conditions"
        href={bcaContractTermsAndConditionsUrl}
        target="_blank"
        rel="noreferrer"
      >
        terms and conditions
      </a>
    </p>

    <button
      className="close-button icon-cross is-icon-4 is-pulled-right is-hidden-mobile"
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onHide();
        }
      }}
    />
  </>
);

type AcceptContractBodyProps = {
  currentOffer?: Offer;
  className?: string;
  earlySettlementAmount?: number;
};

export const AcceptContractBody = ({
  currentOffer,
  className,
  earlySettlementAmount,
}: AcceptContractBodyProps) => (
  <div className="contract-info">
    <table className={`table is-fullwidth ${className}`}>
      <tbody>
        <tr>
          <td>Funding amount</td>
          <td className="contract-info-values" align="right">
            {`R ${formatCurrencyAmountSimple(currentOffer?.Amount)}`}
          </td>
        </tr>
        <tr>
          <td>Repayment frequency</td>
          <td className="contract-info-values" align="right">
            Daily
          </td>
        </tr>
        <tr>
          <td>Approximate repayment term</td>
          <td className="contract-info-values" align="right">
            {`${currentOffer?.Term ?? ''} Months`}
          </td>
        </tr>
        <tr>
          <td>Repayment factor rate</td>
          <td className="contract-info-values" align="right">
            {`${currentOffer?.Rate ?? ''} %`}
          </td>
        </tr>
        <tr>
          <td>Approximate daily repayment</td>
          <td className="contract-info-values" align="right">
            R {formatCurrencyAmountSimple(currentOffer?.DailyAffordability || currentOffer?.ApproximateInstalment)}
          </td>
        </tr>
        <tr>
          <td>Fixed fee</td>
          <td className="contract-info-values" align="right">
            {`R ${formatCurrencyAmountSimple(
              (currentOffer?.TotalRepayments ?? currentOffer?.TotalRepayment ?? 0) - (currentOffer?.Amount ?? 0),
            )}`}
          </td>
        </tr>
        {!earlySettlementAmount && (
          <tr>
            <td>Total repayment</td>
            <td className="contract-info-values" align="right">
              {`R ${formatCurrencyAmountSimple(currentOffer?.TotalRepayment)}`}
            </td>
          </tr>
        )}
        {!!earlySettlementAmount && (
          <tr>
            <td>Current early settlement amount</td>
            <td className="contract-info-values" align="right">
              R {formatCurrencyAmountSimple(earlySettlementAmount)}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

type AcceptContractFootProps = {
  accepted: boolean;
  setAccepted: React.Dispatch<React.SetStateAction<boolean>>;
  disabled: boolean;
  onAcceptOffer: () => void;
};

const AcceptContractFoot = ({
  accepted,
  setAccepted,
  disabled,
  onAcceptOffer,
}: AcceptContractFootProps) => (
  <>
    <div className="accept-checkbox">
      <img
        src={accepted ? TickedCheckboxPortal : UntickedCheckboxPortal}
        alt="toggle-icon"
        onClick={() => setAccepted(!accepted)}
      />
      I have read and agree to the terms and conditions.
    </div>
    <div className="accept-button-container">
      <button
        className="button is-fullwidth accept-button"
        onClick={() => {
          onAcceptOffer();
        }}
        automationid="BCA_Button_Close_Modal"
        disabled={disabled || !accepted}
      >
        {disabled ? <DotLoader color={Colors.BlueGray4} /> : 'Confirm'}
      </button>
    </div>
  </>
);

type AcceptContractModalProps = {
  show: boolean;
  onHide: () => void;
  onAcceptOffer: () => void;
  currentOffer?: Offer;
  disabled: boolean;
};

export default function AcceptContractModal({
  show,
  onHide,
  onAcceptOffer,
  currentOffer,
  disabled,
}: AcceptContractModalProps) {
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (show) {
      TealiumEvents.confirmTCsBCAPageLoad();
    }
  }, [show]);

  return (
    <div className="AcceptContractModal">
      <div className={`modal ${show ? 'is-active' : ''} is-hidden-mobile`}>
        <div className="modal-background"></div>
        <div className="modal-card">
          <header className="container modal-card-head has-text-centered">
            <AcceptContractHead disabled={disabled} onHide={onHide} />
          </header>
          <section className="modal-card-body">
            <AcceptContractBody currentOffer={currentOffer} />
          </section>
          <footer className="modal-card-foot">
            <AcceptContractFoot
              accepted={accepted}
              setAccepted={setAccepted}
              onAcceptOffer={onAcceptOffer}
              disabled={disabled}
            />
          </footer>
        </div>
      </div>
      <InfoTray
        selected={show}
        setSelected={() => onHide()}
        title="Business Cash Advance contract"
        isFullscreen={true}
        className="is-hidden-tablet"
      >
        <p className="top-mobile-page-title navbar-title-mobile is-hidden-tablet">Get Financed</p>
        <AcceptContractHead disabled={disabled} onHide={onHide} />
        <AcceptContractBody currentOffer={currentOffer} />
        <AcceptContractFoot
          accepted={accepted}
          setAccepted={setAccepted}
          onAcceptOffer={onAcceptOffer}
          disabled={disabled}
        />
      </InfoTray>
    </div>
  );
}
